/**
 * Bulk Document Import Service
 * Imports shared resources, standalone blocks (sections), and documents from a ZIP.
 * Uses self-contained helpers (not importHelper) to support standalone block remapping.
 */

import { unzipSync, strFromU8 } from 'fflate';
import {
  BARCODE_ELEMENT,
  ELEMENT_TYPES,
  IMAGE_ELEMENT,
  STATIC_TABLE_ELEMENT,
  TEXT_ELEMENT,
  DYNAMIC_TABLE_BLOCK,
  STANDALONE_BLOCK,
  TEXT_BLOCK,
  STATIC_TABLE_BLOCK,
} from './complexDocumentConstants';
import { VARIABLE_OBJECT_TYPES } from './constants/appConstants';
import {
  addVariableCategory,
  addVariablerelations,
  getAllVariableCategory,
  getAllVariableRelations,
  getAllVariables,
  saveVariable,
} from './services/variablesAPI';
import { getAllImages } from './services/imageAPI';
import { uploadImage } from './services/uploadAPI';
import axiosCall from './services/interceptor';
import {
  createComplexDocument,
  patchComplexDocument,
  createStandaloneBlock,
} from './services/complexDocumentsAPI';
import { updateBusinessLogic } from './services/businessLogicApi';

const DOCUMENTS_FOLDER = 'documents/';
const SECTIONS_FOLDER = 'sections/';

function getKeyFromZip(zip, suffix) {
  const key = Object.keys(zip).find(k => k.endsWith(suffix));
  return key || null;
}

function getNewVariableId(variableId, variableIdMap) {
  if (!variableId) return variableId;
  const id = Number(variableId);
  if (variableIdMap.has(id)) return variableIdMap.get(id).id;
  return variableId;
}

function modifyTextBlockContents(contents, variableIdMap) {
  return contents.map(textContent => {
    if (textContent?.style?.isVariable) {
      const textVariable = String(textContent.content || '').replace(/\{|\}/g, '');
      textContent.content = `{${getNewVariableId(textVariable, variableIdMap)}}`;
    }
    if (textContent?.content === 'custom_logic' && Array.isArray(textContent.contents)) {
      textContent.contents = modifyTextBlockContents(textContent.contents, variableIdMap);
    }
    return textContent;
  });
}

function modifyDynamicTableBlockContents(contents, variableIdMap) {
  return contents.map(tableContent => {
    if (tableContent.headerContent) {
      tableContent.headerContent.contents = modifyTextBlockContents(
        tableContent.headerContent.contents,
        variableIdMap,
      );
    }
    if (tableContent.groupHeaderContent) {
      tableContent.groupHeaderContent.contents = modifyTextBlockContents(
        tableContent.groupHeaderContent.contents,
        variableIdMap,
      );
    }
    if (tableContent.content) {
      tableContent.content.contents = modifyTextBlockContents(
        tableContent.content.contents,
        variableIdMap,
      );
    }
    return tableContent;
  });
}

function modifyBlockContentsSync(contents, variableIdMap, imageUrlMap) {
  for (let i = 0; i < contents.length; i++) {
    const content = contents[i];
    const { contentType, url } = content;
    if (contentType === TEXT_ELEMENT) {
      content.contents = modifyTextBlockContents(content.contents, variableIdMap);
    } else if (contentType === IMAGE_ELEMENT) {
      if (content.style?.isVariable) {
        const oldVariableId = String(content.content || '').replace(/\{|\}/g, '');
        content.content = `{${getNewVariableId(oldVariableId, variableIdMap)}}`;
      }
      if (url && imageUrlMap.has(url)) {
        content.url = imageUrlMap.get(url);
      }
    } else if (contentType === BARCODE_ELEMENT) {
      if (content.style?.isVariable) {
        content.content = `${getNewVariableId(content.content, variableIdMap)}`;
      }
    } else if (contentType === STATIC_TABLE_ELEMENT) {
      content.contents = (content.contents || []).map(tableContent => {
        tableContent.contents = modifyTextBlockContents(tableContent.contents, variableIdMap);
        return tableContent;
      });
    } else if (contentType === ELEMENT_TYPES?.CHART) {
      const updateChartVariables = chartContents => {
        if (!chartContents) return chartContents;
        chartContents.labelVariable = getNewVariableId(chartContents.labelVariable, variableIdMap);
        if (chartContents.valueVariable)
          chartContents.valueVariable = getNewVariableId(chartContents.valueVariable, variableIdMap);
        if (chartContents.descVariable)
          chartContents.descVariable = getNewVariableId(chartContents.descVariable, variableIdMap);
        if (chartContents.seriesValueVariable)
          chartContents.seriesValueVariable = getNewVariableId(
            chartContents.seriesValueVariable,
            variableIdMap,
          );
        if (chartContents.seriesNameVariable)
          chartContents.seriesNameVariable = getNewVariableId(
            chartContents.seriesNameVariable,
            variableIdMap,
          );
        return chartContents;
      };
      if (content.contents) updateChartVariables(content.contents);
      if (content.contents?.dataRequest) updateChartVariables(content.contents.dataRequest);
    }
  }
  return contents;
}

function modifyStaticTableBlockContentsSync(contents, variableIdMap, imageUrlMap) {
  for (let i = 0; i < (contents || []).length; i++) {
    contents[i].contents = modifyBlockContentsSync(
      contents[i].contents || [],
      variableIdMap,
      imageUrlMap,
    );
  }
  return contents;
}

function modifyVariableContract(variableContract, variableIdMap) {
  if (!variableContract || !Array.isArray(variableContract)) return variableContract;
  return variableContract.map(blockVariable => {
    if (VARIABLE_OBJECT_TYPES.includes(blockVariable.type)) {
      blockVariable.content = modifyVariableContract(blockVariable.content, variableIdMap);
    } else if (blockVariable.variableId) {
      const newVariable = variableIdMap.get(blockVariable.variableId);
      if (newVariable) {
        blockVariable.variableId = newVariable.id;
        blockVariable.type =
          newVariable.type === 'ListString' ? 'listOfStrings' : (newVariable.type || '').toLowerCase();
      }
    }
    return blockVariable;
  });
}

function processBlockContents(block, variableIdMap, imageUrlMap) {
  if (block.blockType === DYNAMIC_TABLE_BLOCK) {
    for (let d = 0; d < block.blockDTOList.length; d++) {
      const sub = block.blockDTOList[d];
      if (sub.contents)
        sub.contents = modifyBlockContentsSync(sub.contents, variableIdMap, imageUrlMap);
      if (sub.variableContract)
        sub.variableContract = modifyVariableContract(sub.variableContract, variableIdMap);
    }
    if (block.contents) block.contents = modifyDynamicTableBlockContents(block.contents, variableIdMap);
  } else if (block.blockType === TEXT_BLOCK && block.contents) {
    block.contents = modifyTextBlockContents(block.contents, variableIdMap);
  } else if (block.blockType === STATIC_TABLE_BLOCK && block.contents) {
    block.contents = modifyStaticTableBlockContentsSync(block.contents, variableIdMap, imageUrlMap);
  } else if (block.contents) {
    block.contents = modifyBlockContentsSync(block.contents, variableIdMap, imageUrlMap);
  }
  if (block.variableContract)
    block.variableContract = modifyVariableContract(block.variableContract, variableIdMap);
}

function processBlock(block, variableIdMap, imageUrlMap, blockIdMap) {
  if (block.isStandalone === true && block.blockId && blockIdMap) {
    const newBlockId = blockIdMap.get(block.blockId);
    if (newBlockId != null) block.blockId = newBlockId;
    return;
  }
  block.uuid = block.blockId;
  delete block.blockId;
  processBlockContents(block, variableIdMap, imageUrlMap);
  if (block.blockDTOList) {
    block.blockDTOList.forEach(sub => processBlock(sub, variableIdMap, imageUrlMap, blockIdMap));
  }
}

function processFlow(flow, variableIdMap, imageUrlMap, blockIdMap) {
  flow.uuid = flow.flowId;
  delete flow.flowId;
  const blocks = flow.blocks || [];
  for (let i = 0; i < blocks.length; i++) {
    processBlock(blocks[i], variableIdMap, imageUrlMap, blockIdMap);
  }
  if (flow.header) processBlock(flow.header, variableIdMap, imageUrlMap, blockIdMap);
  if (flow.footer) processBlock(flow.footer, variableIdMap, imageUrlMap, blockIdMap);
}

async function importRelationshipsBulk(relationships, onProgress) {
  const counts = { created: 0, skipped: 0 };
  if (!relationships || relationships.length === 0) return counts;
  let allCategories = [];
  try {
    const catRes = await getAllVariableCategory();
    allCategories = catRes?.data || catRes || [];
  } catch (e) {
    console.warn('getAllVariableCategory failed', e);
  }
  let allRelations = [];
  try {
    const relRes = await getAllVariableRelations();
    allRelations = Array.isArray(relRes) ? relRes : relRes?.data || [];
  } catch (e) {
    console.warn('getAllVariableRelations failed', e);
  }
  const categoryCache = {};
  const getOrCreateCategory = async name => {
    if (categoryCache[name]) return categoryCache[name];
    let cat = (allCategories || []).find(c => (c.name || '').toLowerCase() === (name || '').toLowerCase());
    if (cat) {
      categoryCache[name] = cat.id;
      return cat.id;
    }
    const response = await addVariableCategory({ name });
    const id = response?.data?.id ?? response?.id;
    if (id) {
      categoryCache[name] = id;
      allCategories.push(response?.data || { id, name });
      return id;
    }
    return null;
  };
  for (let i = 0; i < relationships.length; i++) {
    if (onProgress)
      onProgress({ stage: 'relationships', current: i + 1, total: relationships.length });
    const rel = relationships[i];
    const sourceName = rel.sourceCategoryName || rel.source;
    const targetName = rel.targetCategoryName || rel.target;
    if (!sourceName || !targetName) continue;
    const sourceId = await getOrCreateCategory(sourceName);
    const targetId = await getOrCreateCategory(targetName);
    if (!sourceId || !targetId) continue;
    const exists = (allRelations || []).some(
      r =>
        (r.sourceVariableCategoryId ?? r.sourceCategoryId) === sourceId &&
        (r.targetVariableCategoryId ?? r.targetCategoryId) === targetId,
    );
    if (exists) {
      counts.skipped++;
      continue;
    }
    try {
      const response = await addVariablerelations({
        sourceVariableCategoryId: sourceId,
        targetVariableCategoryId: targetId,
        isArray: !!rel.isArray,
      });
      const data = response?.data || response;
      if (data) allRelations.push(data);
      counts.created++;
    } catch (e) {
      console.warn('addVariablerelations failed', e);
    }
  }
  return counts;
}

async function importVariablesBulk(variables, globalVariables, onProgress) {
  const variableIdMap = new Map();
  const counts = { created: 0, skipped: 0 };
  if (!variables || variables.length === 0) return { variableIdMap, counts };
  let allCategories = [];
  try {
    const res = await getAllVariableCategory();
    allCategories = res?.data || res || [];
  } catch (e) {
    console.warn('getAllVariableCategory failed', e);
  }
  const categoryCache = {};
  const getUpdatedName = (variableCatName, name, type) => {
    const placeHolderText = `${variableCatName}.${name}`;
    const existing = (globalVariables || []).find(
      g => (g.placeHolderText || '').toLowerCase() === placeHolderText.toLowerCase(),
    );
    const typeMismatch = existing && existing.type !== type;
    if (typeMismatch) return getUpdatedName(variableCatName, name + '_01', type);
    return [existing, name, typeMismatch];
  };
  for (let i = 0; i < variables.length; i++) {
    if (onProgress) onProgress({ stage: 'variables', current: i + 1, total: variables.length });
    const v = variables[i];
    const variableCatName = v.variableCategoryName;
    const name = v.name;
    const type = v.type;
    const id = v.id;
    let catId = (allCategories || []).find(
      c => (c.name || '').toLowerCase() === (variableCatName || '').toLowerCase(),
    )?.id;
    if (!catId && variableCatName && !categoryCache[variableCatName]) {
      try {
        const res = await addVariableCategory({ name: variableCatName });
        catId = res?.data?.id ?? res?.id;
        if (catId) {
          categoryCache[variableCatName] = catId;
          if (res?.data) allCategories.push(res.data);
        }
      } catch (e) {
        console.warn('addVariableCategory failed', e);
      }
    }
    if (!catId) catId = categoryCache[variableCatName];
    if (!catId) continue;
    const [existingVariable, uName, typeMismatch] = getUpdatedName(variableCatName, name, type);
    if (existingVariable && !typeMismatch) {
      variableIdMap.set(id, existingVariable);
      counts.skipped++;
      continue;
    }
    const newVariable = {
      name: uName,
      placeHolderText: v.placeHolderText || `${variableCatName}.${uName}`,
      displayName: v.displayName,
      variableCategoryId: catId,
      variableCategoryName: variableCatName,
      length: v.length,
      exampleValue: v.exampleValue,
      type: type,
      count: v.count,
      mandatory: v.mandatory === true,
      checkLength: v.checkLength === true,
    };
    try {
      const response = await saveVariable(newVariable);
      const errs = response?.errors;
      if (errs && errs.length > 0) continue;
      const newId = response?.id;
      if (newId) {
        const newVar = { ...newVariable, id: newId };
        variableIdMap.set(id, newVar);
        counts.created++;
      }
    } catch (e) {
      console.warn('saveVariable failed', e);
    }
  }
  console.log({ variableIdMap });
  return { variableIdMap, counts };
}

async function importImagesBulk(images, onProgress) {
  const imageUrlMap = new Map();
  const counts = { created: 0, skipped: 0 };
  if (!images || images.length === 0) return { imageUrlMap, counts };
  let existingImages = [];
  try {
    const res = await getAllImages({});
    existingImages = res?.data || res || [];
  } catch (e) {
    console.warn('getAllImages failed', e);
  }
  const byName = (existingImages || []).reduce((acc, img) => {
    const n = (img.name || img.path || '').trim();
    if (n) acc[n.toLowerCase()] = img;
    return acc;
  }, {});
  for (let i = 0; i < images.length; i++) {
    if (onProgress) onProgress({ stage: 'images', current: i + 1, total: images.length });
    const img = images[i];
    const oldUrl = img.imageLink || img.url || img.imageAddress;
    const name = (img.name || img.path || '').trim() || `image_${i}`;
    const existing = byName[name.toLowerCase()];
    if (existing && (existing.imageLink || existing.url)) {
      const existingUrl = existing.imageLink || existing.url;
      imageUrlMap.set(oldUrl, existingUrl);
      counts.skipped++;
      continue;
    }
    try {
      const response = await axiosCall('get', oldUrl, { responseType: 'blob' }, false);
      const blob = response?.data;
      if (!blob) continue;
      const ext = (name.split('.').pop() || 'png').replace(/[^a-z0-9]/i, '') || 'png';
      const file = new File([blob], name.endsWith(`.${ext}`) ? name : `${name}.${ext}`);
      const uploadRes = await uploadImage(file, file.name);
      const newUrl = uploadRes?.imageLink;
      if (newUrl) {
        imageUrlMap.set(oldUrl, newUrl);
        counts.created++;
      }
    } catch (e) {
      console.warn('Image upload failed', name, e);
    }
  }
  return { imageUrlMap, counts };
}

function normalizeZip(zipContents) {
  if (zipContents instanceof ArrayBuffer)
    return unzipSync(new Uint8Array(zipContents));
  if (zipContents instanceof Uint8Array) return unzipSync(zipContents);
  if (typeof zipContents === 'object' && zipContents !== null && !zipContents.filename)
    return zipContents;
  return unzipSync(zipContents);
}

export async function importSharedResources(zipContents, globalVariables, onProgress) {
  const zipObj = normalizeZip(zipContents);
  const keys = Object.keys(zipObj).filter(k => !k.endsWith('/'));
  const getKey = suffix => keys.find(k => k.endsWith(suffix)) || null;

  const relationshipsKey = getKey('relationships.json');
  const variablesKey = getKey('variables.json');
  const imagesKey = getKey('images.json');

  let relationships = [];
  let variables = [];
  let images = [];
  if (relationshipsKey) {
    try {
      const raw = zipObj[relationshipsKey];
      const str = raw && raw.length ? strFromU8(raw) : '';
      if (str) relationships = JSON.parse(str);
    } catch (e) {
      console.warn('Parse relationships.json failed', e);
    }
  }
  if (variablesKey) {
    try {
      const raw = zipObj[variablesKey];
      const str = raw && raw.length ? strFromU8(raw) : '';
      if (str) variables = JSON.parse(str);
    } catch (e) {
      console.warn('Parse variables.json failed', e);
    }
  }
  if (imagesKey) {
    try {
      const raw = zipObj[imagesKey];
      const str = raw && raw.length ? strFromU8(raw) : '';
      if (str) images = JSON.parse(str);
    } catch (e) {
      console.warn('Parse images.json failed', e);
    }
  }

  const relCounts = await importRelationshipsBulk(relationships, onProgress);
  const { variableIdMap, counts: varCounts } = await importVariablesBulk(
    variables,
    globalVariables || [],
    onProgress,
  );
  const { imageUrlMap, counts: imgCounts } = await importImagesBulk(images, onProgress);

  return {
    variableIdMap,
    imageUrlMap,
    counts: {
      relationships: relCounts,
      variables: varCounts,
      images: imgCounts,
    },
  };
}

function discoverSectionFolders(zipObj) {
  const keys = Object.keys(zipObj);
  const folders = new Set();
  const sectionTag = SECTIONS_FOLDER.replace(/\/$/, '').toLowerCase();
  keys.forEach(k => {
    const parts = k.replace(/\\/g, '/').split('/').filter(Boolean);
    const idx = parts.findIndex(p => p.toLowerCase() === sectionTag);
    if (idx >= 0 && idx + 1 < parts.length) folders.add(parts[idx + 1]);
  });
  return Array.from(folders);
}

function discoverDocumentFolders(zipObj) {
  const keys = Object.keys(zipObj);
  const folders = new Set();
  const documentTag = DOCUMENTS_FOLDER.replace(/\/$/, '').toLowerCase();
  keys.forEach(k => {
    const parts = k.replace(/\\/g, '/').split('/').filter(Boolean);
    const idx = parts.findIndex(p => p.toLowerCase() === documentTag);
    if (idx >= 0 && idx + 1 < parts.length) folders.add(parts[idx + 1]);
  });
  return Array.from(folders);
}

function getZipEntryKey(zipObj, path) {
  const keys = Object.keys(zipObj);
  const normalized = path.replace(/^\//, '').replace(/\/+/g, '/');
  const normalizedLower = normalized.toLowerCase();
  return keys.find(k => k.toLowerCase() === normalizedLower || (k.toLowerCase().includes('/') && k.toLowerCase().endsWith(normalizedLower))) || null;
}

function stripBlockIds(block) {
  if (!block) return block;
  const b = { ...block };
  delete b.blockId;
  delete b.id;
  if (Array.isArray(b.blockDTOList)) b.blockDTOList = b.blockDTOList.map(stripBlockIds);
  return b;
}

function processSectionBlocks(blocks, variableIdMap, imageUrlMap) {
  const out = [];
  for (let i = 0; i < (blocks || []).length; i++) {
    const block = JSON.parse(JSON.stringify(blocks[i]));
    delete block.blockId;
    delete block.id;
    processBlockContents(block, variableIdMap, imageUrlMap);
    if (block.blockDTOList)
      block.blockDTOList = block.blockDTOList.map(sub => stripBlockIds(JSON.parse(JSON.stringify(sub))));
    out.push(block);
  }
  return out;
}

function getResponseErrorMessage(res) {
  if (!res) return '';
  if (Array.isArray(res.errors) && res.errors.length) {
    return res.errors.map(e => e?.description ?? e?.message ?? (typeof e === 'string' ? e : '')).filter(Boolean).join(' ');
  }
  if (typeof res.error === 'string') return res.error;
  if (res.error?.description) return res.error.description;
  if (res.error?.message) return res.error.message;
  if (res.status && typeof res.status === 'string') return res.status;
  return '';
}

function isNameExistsError(errOrMessage) {
  const msg = typeof errOrMessage === 'string'
    ? errOrMessage
    : (errOrMessage?.message ?? errOrMessage?.description ?? String(errOrMessage || ''));
  const lower = msg.toLowerCase();
  return (
    lower.includes('name') &&
    (lower.includes('exist') || lower.includes('already') || lower.includes('duplicate') || lower.includes('unique'))
  );
}

export async function importSections(
  zipContents,
  variableIdMap,
  imageUrlMap,
  resourceGroupId,
  sectionCategoryId,
  onProgress,
  allBlocks
) {

  const existingBlockIds = allBlocks.reduce((acc, block) => {
    acc.set(block.parentBlockName, block.parentBlockId);
    return acc;
  }, new Map());
  console.log(" existingBlockIds :: ", existingBlockIds);
  const zipObj = normalizeZip(zipContents);
  const blockIdMap = new Map();
  const sectionFolders = discoverSectionFolders(zipObj);
  const prefix = SECTIONS_FOLDER;

  for (let s = 0; s < sectionFolders.length; s++) {
    const folderName = sectionFolders[s];
    if (onProgress)
      onProgress({
        stage: 'sections',
        sectionIndex: s,
        totalSections: sectionFolders.length,
        folderName,
      });
    const langOrder = ['en', 'de', 'fr', 'nl'];
    let groupId = null;
    let firstParentBlockIdFromExport = null;
    let oldBlockId = null;
    for (let li = 0; li < langOrder.length; li++) {
      const lang = langOrder[li];
      let parentBlockName = `${folderName}_${lang}`;

      const key = getZipEntryKey(zipObj, `${prefix}${folderName}/${lang}.json`);
      if (!key) continue;
      let jsonStr;
      try {
        const raw = zipObj[key];
        jsonStr = raw && raw.length ? strFromU8(raw) : null;
      } catch (e) {
        continue;
      }
      if (!jsonStr) continue;
      let template;
      try {
        template = JSON.parse(jsonStr);
      } catch (e) {
        if (onProgress) onProgress({ stage: 'sections', error: `${folderName}/${lang}.json parse error` });
        continue;
      }
      const flows = template.flows || [];
      const firstFlow = flows[0];
      if (!firstFlow || !firstFlow.blocks) continue;
      if (existingBlockIds.get(parentBlockName)) {
        blockIdMap.set(firstFlow.flowId, existingBlockIds.get(parentBlockName));
        continue;
      }
      oldBlockId = firstFlow.flowId;
      if (firstParentBlockIdFromExport == null && firstFlow.flowId)
        firstParentBlockIdFromExport = firstFlow.flowId;
      const processedBlocks = processSectionBlocks(
        firstFlow.blocks,
        variableIdMap,
        imageUrlMap,
      );
      const style = template.style || { fontFamily: 'Arial', fontSize: 13, lineSpacing: 1.1 };
      let attempt = 0;
      let response = null;
      while (true) {
        try {
          const payload = {
            categoryId: sectionCategoryId,
            resourceGroupId,
            parentBlockName,
            isReferenced: true,
            blocks: processedBlocks,
            type: 'content',
            language: lang,
            style,
          };
          if (groupId) payload.groupId = groupId;
          response = await createStandaloneBlock(payload);
          break;
          const errMsg = getResponseErrorMessage(response);
          if (errMsg && isNameExistsError(errMsg) && attempt < 10) {
            attempt++;
            parentBlockName = `${folderName}_${lang}_${String(attempt).padStart(2, '0')}`;
          } else {
            if (onProgress) onProgress({ stage: 'sections', error: errMsg || 'Create failed', folderName, language: lang });
            break;
          }
        } catch (e) {
          if (isNameExistsError(e) && attempt < 10) {
            attempt++;
            parentBlockName = `${folderName}_${lang}_${String(attempt).padStart(2, '0')}`;
          } else {
            if (onProgress) onProgress({ stage: 'sections', error: e?.message || String(e), folderName, language: lang });
            break;
          }
        }
      }
      if (response?.data != null) {
        const newParentBlockId = response.data.parentBlockId || response.data.id;
        blockIdMap.set(oldBlockId, newParentBlockId);
        if (groupId == null && response.data?.groupId) groupId = response.data.groupId;
      }
    }
  }
  return blockIdMap;
}

export async function importCommunication(
  folderName,
  langFiles,
  variableIdMap,
  imageUrlMap,
  blockIdMap,
  resourceGroupId,
  documentCategoryId,
  onProgress,
) {
  const results = { languages: {}, communicationId: null, documentId: null, groupId: null };
  let firstCommunicationId = null;
  let firstGroupId = null;
  const langOrder = ['en', 'de', 'fr', 'nl'];
  for (let li = 0; li < langOrder.length; li++) {
    const lang = langOrder[li];
    const content = langFiles[lang];
    if (!content) continue;
    if (onProgress) onProgress({ stage: 'document', folderName, language: lang });
    let template;
    try {
      template = JSON.parse(JSON.stringify(content));
    } catch (e) {
      results.languages[lang] = { success: false, error: 'Parse error' };
      continue;
    }
    delete template.documentId;
    const flows = template.flows || [];
    flows.forEach(flow => processFlow(flow, variableIdMap, imageUrlMap, blockIdMap));
    const firstFlow = flows[0];
    const style = template.style || { fontFamily: 'Arial', fontSize: 13 };
    let documentName = folderName;
    let attempt = 0;
    let createResponse = null;
    while (true) {
      try {
        const minimalPayload = {
          documentName,
          displayName: documentName,
          language: lang,
          categoryId: documentCategoryId,
          resourceGroupId,
          style,
          flows: [
            {
              orderNumber: 1,
              flowName: firstFlow?.flowName || '',
              style: firstFlow?.style || {
                padding: { left: 0, right: 0, top: 0, bottom: 0 },
                width: 794,
                height: 1123,
                backgroundColor: '#ffffff',
              },
              paperSize: firstFlow?.paperSize || 'a4',
              paperOrientation: firstFlow?.paperOrientation || 'portrait',
              header: null,
              footer: null,
              blocks: [],
            },
          ],
        };
        if (firstCommunicationId) {
          minimalPayload.communicationId = firstCommunicationId;
          minimalPayload.newCommunicationId = firstCommunicationId;
        }
        if (firstGroupId) minimalPayload.groupId = firstGroupId;
        createResponse = await createComplexDocument(minimalPayload, false);
        if (createResponse?.data?.documentId) break;
        const errMsg = getResponseErrorMessage(createResponse);
        if (errMsg && isNameExistsError(errMsg) && attempt < 10) {
          attempt++;
          documentName = `${folderName}_${String(attempt).padStart(2, '0')}`;
        } else {
          results.languages[lang] = { success: false, error: errMsg || 'Create failed' };
          break;
        }
      } catch (e) {
        if (isNameExistsError(e) && attempt < 10) {
          attempt++;
          documentName = `${folderName}_${String(attempt).padStart(2, '0')}`;
        } else {
          results.languages[lang] = { success: false, error: e?.message || String(e) };
          break;
        }
      }
    }
    if (!createResponse?.data?.documentId) {
      if (!results.languages[lang]) results.languages[lang] = { success: false, error: 'Create failed' };
      continue;
    }
    const documentId = createResponse.data.documentId;
    const communicationId = createResponse.data.communicationId;
    const flowId = createResponse.data.flowId;
    const groupId = createResponse.data.groupId;
    let localFlowMap = new Map(), localBlockMap = new Map();
    if (!firstCommunicationId) {
      firstCommunicationId = communicationId;
      firstGroupId = groupId;
      results.communicationId = communicationId;
      results.documentId = documentId;
      results.groupId = groupId;
    }
    const flowsWithId = flows.map((flow, idx) =>
      idx === 0 && flowId ? { ...flow, flowId } : flow,
    );
    let patchResponse = null;
    try {
      patchResponse = await patchComplexDocument(
        {
          documentId,
          flows: flowsWithId,
          name: documentName,
          language: lang,
          variableContract: template.variableContract || [],
          style,
        },
        null,
        null,
        false,
        false,
        false,
        true
      );
      localFlowMap = patchResponse.data.data?.flows.reduce((acc, flow) => {
        acc.set(flow.uuid, flow.flowId);
        return acc;
      }, new Map());
      localBlockMap = patchResponse.data.data?.flows.reduce((acc, flow) => {
        flow.blocks.forEach(block => {
          acc.set(block.uuid, block.blockId);
        });
        return acc;
      }, new Map());
    } catch (e) {
      results.languages[lang] = { success: true, documentId, communicationId, patchError: e?.message };
      continue;
    }
    console.log("localMaps :: ", localFlowMap, localBlockMap);

    const businessLogics = template.businessLogics || template._preservedBusinessLogics;
    if (businessLogics && Array.isArray(businessLogics)) {
      const cloned = businessLogics.map(bl => {
        const c = JSON.parse(JSON.stringify(bl));
        c.documentId = documentId;
        c.id = null;
        (c.variables || []).forEach(v => {
          if (v.variableId && variableIdMap.has(v.variableId))
            v.variableId = variableIdMap.get(v.variableId).id;
        });
        (c.rules || []).forEach(r => {
          r.id = null;
          if (r.variableId && variableIdMap.has(r.variableId))
            r.variableId = variableIdMap.get(r.variableId).id;
          if (r.blockId) {
            if (localBlockMap && localBlockMap.has(r.blockId)) {
              r.blockId = localBlockMap.get(r.blockId);
            }
          } else if (r.flowId) {
            if (localFlowMap && localFlowMap.has(r.flowId)) {
              r.flowId = localFlowMap.get(r.flowId);
            }
          }
          (r.conditions || []).forEach(cond => {
            if (cond.variableId && variableIdMap.has(cond.variableId))
              cond.variableId = variableIdMap.get(cond.variableId).id;
          });
        });
        return c;
      });
      for (const bl of cloned) {
        try {
          await updateBusinessLogic(bl);
        } catch (blErr) {
          console.warn('Business logic create failed', blErr);
        }
      }
    }
    results.languages[lang] = { success: true, documentId, communicationId };
  }
  return results;
}

export function exportMapCache(variableIdMap, imageUrlMap, blockIdMap) {
  const toEntries = m => (m instanceof Map ? Array.from(m.entries()) : []);
  return JSON.stringify({
    variableIdMap: toEntries(variableIdMap),
    imageUrlMap: toEntries(imageUrlMap),
    blockIdMap: toEntries(blockIdMap),
  });
}

export function importMapCache(jsonString) {
  let data;
  try {
    data = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
  } catch (e) {
    return null;
  }
  const fromEntries = arr => {
    if (!Array.isArray(arr)) return new Map();
    return new Map(arr);
  };
  return {
    variableIdMap: fromEntries(data.variableIdMap),
    imageUrlMap: fromEntries(data.imageUrlMap),
    blockIdMap: fromEntries(data.blockIdMap),
  };
}

export function parseBulkImportZip(zipContents) {
  const zipObj = normalizeZip(zipContents);
  const sectionFolders = discoverSectionFolders(zipObj);
  const documentFolders = discoverDocumentFolders(zipObj);
  const prefixSec = SECTIONS_FOLDER;
  const prefixDoc = DOCUMENTS_FOLDER;
  const getKey = (pre, folder, lang) => getZipEntryKey(zipObj, `${pre}${folder}/${lang}.json`);
  const sectionLangFiles = {};
  sectionFolders.forEach(f => {
    sectionLangFiles[f] = {};
    ['en', 'de', 'fr', 'nl'].forEach(lang => {
      const key = getKey(prefixSec, f, lang);
      if (!key) return;
      try {
        const raw = zipObj[key];
        sectionLangFiles[f][lang] = raw && raw.length ? JSON.parse(strFromU8(raw)) : null;
      } catch (e) {
        sectionLangFiles[f][lang] = null;
      }
    });
  });
  const documentLangFiles = {};
  documentFolders.forEach(f => {
    documentLangFiles[f] = {};
    ['en', 'de', 'fr', 'nl'].forEach(lang => {
      const key = getKey(prefixDoc, f, lang);
      if (!key) return;
      try {
        const raw = zipObj[key];
        documentLangFiles[f][lang] = raw && raw.length ? JSON.parse(strFromU8(raw)) : null;
      } catch (e) {
        documentLangFiles[f][lang] = null;
      }
    });
  });
  return {
    sectionFolders,
    documentFolders,
    sectionLangFiles,
    documentLangFiles,
    zipObj,
  };
}
